import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { ChannelIdentity } from '@msdyn365-commerce/retail-proxy';
import { getChannelsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';
import { buildCacheKey, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';

import { BuildMilConfiguredChannelIdentities, IMilAdditionalChannel } from '../utils/MilConfiguredChannels';

/**
 * Interface of Channel Identity.
 */
interface IChannelIdentity {
    channelIdentities: ChannelIdentity[];
}

/**
 * Shape of the order history module configuration this action reads.
 */
interface IAdditionalChannelsConfig {
    additionalChannels?: IMilAdditionalChannel[];
}

/**
 * Calls the Retail API and returns the channels.
 * @param ctx The action context.
 * @returns List of channel identity.
 */
const getChannels = () => async (ctx: IActionContext): Promise<ChannelIdentity[]> => {
    return getChannelsAsync({
        callerContext: ctx,
        queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx)
    });
};

/**
 * Action input.
 *
 * The configured channels are carried on the input because the action itself only receives the
 * input and the action context - it has no access to module configuration.
 */
export class GetAllChannelIdentitiesInput implements IActionInput {
    public readonly additionalChannels: IMilAdditionalChannel[];

    private readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings, additionalChannels: IMilAdditionalChannel[]) {
        this.apiSettings = apiSettings;
        this.additionalChannels = additionalChannels;
    }

    // Include configuration so different module configurations cannot share one request result.
    public getCacheKey = () =>
        `${buildCacheKey('ChannelIdentities', this.apiSettings)}-${this.additionalChannels
            .map(channel => `${channel.channelRecordId ?? ''}:${channel.channelDisplayName ?? ''}`)
            .join('|')}`;

    public getCacheObjectType = () => 'ChannelIdentities';

    public dataCacheType = (): CacheType => 'request';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData Action Data Input.
 * @returns Input object.
 */
const createAllChannelIdentitiesInput = (inputData: ICreateActionContext<IGeneric<IAny>>) => {
    const config = inputData.config as IAdditionalChannelsConfig | undefined;

    return new GetAllChannelIdentitiesInput(inputData.requestContext.apiSettings, config?.additionalChannels ?? []);
};

/**
 * Returns the channels from the GetChannels API together with any channels supplied through module
 * configuration.
 *
 * F&O blocks call center channels from being copied to the AX channel table, so GetChannels never
 * returns them. Appending the configured channels here puts them into the order history "Origin"
 * filter - which filters server side on channel record id - and lets their names resolve.
 * @param input Input data.
 * @param context Action context.
 * @returns Channel Identity.
 */
export async function getAllChannelsAction(input: GetAllChannelIdentitiesInput, context: IActionContext): Promise<IChannelIdentity> {
    if (!context) {
        throw new Error('getAllChannelsAction - Action context cannot be null/undefined');
    }
    const channels: ChannelIdentity[] = await getChannels()(context);
    const configuredChannels = BuildMilConfiguredChannelIdentities(input.additionalChannels, channels);
    const allChannels = [...channels, ...configuredChannels];
    allChannels.sort((a, b) => (a.Name ?? '').localeCompare(b.Name ?? ''));
    return { channelIdentities: allChannels };
}

/**
 * Get all channels data action.
 */
export const getAllChannelsActionDataAction = createObservableDataAction({
    id: '@millcraft/order-history/get-all-channels',
    action: <IAction<IChannelIdentity>>getAllChannelsAction,
    input: createAllChannelIdentitiesInput
});

export default getAllChannelsActionDataAction;
